import { COOKIE_CHANGE_EVENT, DEFAULT_COOKIE } from "../constants/cookie-constants";
import { localizationDictionary, localizationRtl } from "../constants/localization-dictionary";
import { APP_ID, BANNER_ID, CONTAINER_ID, CUSTOMIZE_ID, ERROR_MESSAGE_MODAL_ID, CCBA_MODAL_ID } from "../constants/ux-constants";
import { getConsentCookie as getConsentCookieCore, getId, ValidationFields } from "../cookie";
import CookieDefinitions, { COOKIE_CATEGORIES, ESSENTIAL, PERFORMANCE } from "../definitions/cookie-definitions";
import UXDefinitions from "../definitions/ux-definitions";
import { render, act } from "../jsx-act";
import { Log } from "../logger";
import domReady from "../ready";
import ConsentBanner from "./consent-banner/consent-banner";
import ConsentSelector from "./consent-selector/consent-selector";
import ErrorMessageModal from "./consent-selector/error-message-modal";
import "./ux-components.scss";
import LogSources = UXDefinitions.LogSources;
import { closeModal, openModal } from "./utilities/modal-controls";
import { METRICS } from "../telemetry/constants";
import { CcbaSelector } from "./consent-ccba/ccba-selector";

interface ShortbreadUiOptions {
    parent: HTMLElement | undefined,
    language: UXDefinitions.LanguageCodesNormalized,
    onSaveConsent: (consent: CookieDefinitions.ConsentCookie) => void,
    onSavePermanentConsent: (PermanentConsent: CookieDefinitions.PermanentConsentCookie) => void,
    getConsentCookie: () => ReturnType<typeof getConsentCookieCore>,
    log: Log,
    onModalClose?: () => void,
    hasConsoleNavFooter?: boolean,
    runtimeEnvironment?: UXDefinitions.RuntimeEnvironment,
    domain: string | undefined,
    handleValidation: (fields: ValidationFields, source: string) => boolean
}

export function isChecked(category: CookieDefinitions.CookieCategory): boolean {
    return document
        .querySelector(`label[data-id=awsccc-u-cb-${category}-label] input`)!
        .getAttribute("checked") === "";
}

function renderUi(options: ShortbreadUiOptions) {

    const localizedText: UXDefinitions.LocalizationSet = localizationDictionary[options.language]
    const isRtl = localizationRtl.indexOf(options.language) > -1;
    const conditionalClasses = isRtl ? "awsccc-Rtl" : "";

    function _getBannerEl(): HTMLElement {
        return document.querySelector<HTMLElement>(`div[data-id=${BANNER_ID}]`)!
    }

    function setCheckboxStatus(category: CookieDefinitions.CookieCategory, on: boolean) {
        const labelEl = document.querySelector(`label[data-id=awsccc-u-cb-${category}-label]`);
        const classList = labelEl!.classList;
        const inputEl = labelEl!.querySelector("input")

        if (on) {
            inputEl!.setAttribute("checked", "")
            classList.add("awsccc-u-cb-checkbox-active");
        } else {
            classList.remove("awsccc-u-cb-checkbox-active");
            inputEl!.removeAttribute("checked")
        }
    }

    const handleCheckboxToggle: UXDefinitions.HandleCheckboxToggle = ({ event, category }) => {
        if (event.target.getAttribute("type") === "checkbox" || event.target.getAttribute("class") === "awsccc-cs-s-title" || event.target.getAttribute("class")?.includes("awsccc-u-cb-label")) {
            setCheckboxStatus(category, !isChecked(category));
        }
    }

    /**
     * Emits a cookie change event to notify other parts of the application that the cookie has changed
     * This is useful for scenarios where CCBA radio button needs to be updated based on advertizing cookie change
     */
    const emitCookieChangeEvent = () => {
        const cookieChangeEvent = new CustomEvent(COOKIE_CHANGE_EVENT, {
            detail: {
                cookie: options.getConsentCookie()
            }
        });
        document.dispatchEvent(cookieChangeEvent);
    }

    const handleSaveClick = (context: 'acceptAll' | 'customize' | 'decline') => (cookie: CookieDefinitions.ConsentCookie, source) => {
        const firstTabEl = _getBannerEl().querySelector<HTMLElement>(`div[data-id=awsccc-cb-tabstart]`)
        _getBannerEl().style.display = 'none';
        firstTabEl!.setAttribute("tabindex", "-1")

        if (context === 'customize') {
            closeConsentSelector();
        }

        if (!options.handleValidation({ domain: options.domain}, 'saveBtnClick')) {
            showErrorMessage();
            return;
        }
        // ccba cookie should match the advertising cookie
        cookie.ccba = cookie.advertising;
        options.onSaveConsent(cookie);
        
        if (!isAWSConsole()) {
            options.onSavePermanentConsent({ ccba: cookie.ccba });
            emitCookieChangeEvent();
        }

        options.log("info")(context, { detail: "Save Consent Clicked", source, cookie: options.getConsentCookie(), uuid: getId() });
    }

    const handleCancelClick = (context: string) => (source) => {
        options.log("info")(context, { detail: "Customize Modal Cancel Button Clicked", source, cookie: options.getConsentCookie(), uuid: getId() });
        closeConsentSelector();
    }

    // for error message modal
    const handleDismissClick = () => {
        closeErrorMessageModal();
    }

    const showBanner: UXDefinitions.HandleShowBanner = () => {
        const firstTabEl = _getBannerEl().querySelector<HTMLElement>(`div[data-id=awsccc-cb-tabstart]`)
        _getBannerEl().style.display = 'block';
        firstTabEl!.setAttribute("tabindex", "0")
        firstTabEl!.focus({
            preventScroll: true
        });
    }

    const getCookie = () => {
        return options.getConsentCookie() || DEFAULT_COOKIE;
    }

    const updateCheckboxStatusFromCookie = () => {
        const existingCookie = options.getConsentCookie();
        const cookie = existingCookie || DEFAULT_COOKIE;
        COOKIE_CATEGORIES.filter(c => c !== ESSENTIAL).forEach(category => {
            const checked = category === PERFORMANCE && !existingCookie ? true : cookie[category];
            setCheckboxStatus(category, checked);
        });
    }

    const showConsentSelector: UXDefinitions.HandleShowConsent = (source) => {
        updateCheckboxStatusFromCookie();
        openModal(CUSTOMIZE_ID);

        options.log("info")(METRICS.MANNUAL_OPEN_PREFERENCE_MODAL, { detail: "Customize Consent Clicked", source, cookie: options.getConsentCookie(), uuid: getId() });
    }

    const handleCcbaSaveClick = (context: "ccbaPreferenceSaved") => (cookie: CookieDefinitions.ConsentCookie, source) => {
        if (!options.handleValidation({ domain: options.domain }, 'saveBtnClick')) {
            showErrorMessage();
            return;
        }
        // Pass only the ccba property as a PermanentConsentCookie
        options.onSavePermanentConsent({ ccba: cookie.ccba });

        // advertising cookie should match the ccba cookie
        cookie.advertising = cookie.ccba;
        options.onSaveConsent(cookie);
        emitCookieChangeEvent();
        closeCcbaPreferenceModal();
        options.log("info")(context, { 
            detail: "CCBA Preference Saved", 
            source, 
            cookie: options.getConsentCookie(), 
            uuid: getId()
        });
    }

    const handleCcbaCancelClick = (context: string) => (source) => {
        options.log("info")(context, { detail: "CCBA Modal Dismissed", source, cookie: options.getConsentCookie(), uuid: getId() });
        closeCcbaPreferenceModal();
    }

    const showCcbaPreferenceModal = (source) => {
        // If the CCBA modal markup doesnt exist, an older version of Shortbread probably loaded first.
        // Inject it into the older shortbread's container.
        if (!document.querySelector(`[data-id="${CCBA_MODAL_ID}"]`)) {
            render(document.getElementById(APP_ID) as Element, getCCBASelector());
        }
        openModal(CCBA_MODAL_ID);

        options.log("info")(METRICS.MANUAL_OPEN_CCBA_MODAL, { detail: "CCBA Modal Opened", source, cookie: options.getConsentCookie(), uuid: getId() });
    };

    const showErrorMessage: UXDefinitions.HandleShowErrorMessage = () => {
        openModal(ERROR_MESSAGE_MODAL_ID);
    }

    const closeConsentSelector = () => {
        closeModal(CUSTOMIZE_ID);

        /**
         * Accessibility - If modal is closed but banner is still open
         * which occurs when a user cancels, focus back onto the banner
         */
        if (_getBannerEl().style.display === "block") {
            const firstTabEl = _getBannerEl().querySelector<HTMLElement>(`div[data-id=awsccc-cb-tabstart]`)
            firstTabEl!.focus({
                preventScroll: true
            });
        }
        /**
         * Accessibility - optional function that can be called after closing the modal.
         * Can be used to focus on another element that opened the modal.
         */
        if (options.onModalClose) {
            options.onModalClose()
        }
    };

    const closeCcbaPreferenceModal = () => {
        closeModal(CCBA_MODAL_ID);
    }

    const closeErrorMessageModal = () => {
        closeModal(ERROR_MESSAGE_MODAL_ID);
    };

    const getCCBASelector = () => {
        return (
            <CcbaSelector
                runtimeEnvironment={options.runtimeEnvironment}
                isRtl={isRtl}
                content={localizedText.ccbaSelector}
                darkModeEnabled={options.hasConsoleNavFooter}
                getConsentCookie={options.getConsentCookie}
                handleSaveClick={handleCcbaSaveClick("ccbaPreferenceSaved")}
                handleCancelClick={handleCcbaCancelClick("ccbaModalDismissed")}
            />
        )
    }

    domReady(() => {
        if (document.querySelector(`#${CONTAINER_ID}`)) {
            // The Shortbread components have already been placed in the DOM, no need to add them again
            return;
        }
        render(options.parent || document.body,
            <div id={CONTAINER_ID}>
                <div id={APP_ID} class={conditionalClasses}>
                    <ConsentBanner
                        showConsentSelector={showConsentSelector}
                        handleSaveClick={handleSaveClick("acceptAll")}
                        handleDeclineClick={handleSaveClick("decline")}
                        localizedText={localizedText.consentBanner}
                        hasConsoleNavFooter={options.hasConsoleNavFooter}
                        runtimeEnvironment={options.runtimeEnvironment}
                        getConsentCookie={options.getConsentCookie}
                    />
                    <ConsentSelector
                        consentState={getCookie()}
                        handleSaveClick={handleSaveClick("customize")}
                        handleCancelClick={handleCancelClick("cancel")}
                        handleCheckboxToggle={handleCheckboxToggle}
                        localizedText={localizedText.consentSelector}
                        darkModeEnabled={options.hasConsoleNavFooter}
                        runtimeEnvironment={options.runtimeEnvironment}
                        getConsentCookie={options.getConsentCookie}
                    />
                    {getCCBASelector()}
                    <ErrorMessageModal
                        darkModeEnabled={options.hasConsoleNavFooter}
                        handleDismissClick={handleDismissClick}
                        localizedText={localizedText.errorMessage}
                    />
                </div>
            </div>
        );
    });

    return {
        showConsentSelector(source: LogSources | undefined) {
            domReady(() => {
                showConsentSelector(source);
            })
        },
        showCcbaPreferenceModal(source: LogSources | undefined) {
            domReady(() => {
                showCcbaPreferenceModal(source);
            })
        },
        showBanner(andThen: () => void) {
            domReady(() => {
                showBanner();
                andThen();
            });
        },
        showErrorMessage() {
            domReady(() => {
                showErrorMessage();
            })
        }
    };
}

export default {
    createShortbreadUi(options: ShortbreadUiOptions) {
        // Publish init metric with sample rate (10%).
        const randomNum = Math.random();
        if (randomNum < 0.1) {
            const { log, domain } = options;
            log("info")(METRICS.UI_MOUNTED, { domain });
        }
        return renderUi(options);
    },
};
export function isAWSConsole() {
    const args = ['ConsoleNavService', 'Model', 'currentService']
    return getNestedObj(args, window) != null
}

export function getNestedObj(args: string[], obj: object): any {
  if (args.length === 0) {
    return null
  }
  for (let i = 0; i < args.length; i++) {
    if (!obj || !Object.hasOwnProperty.call(obj, args[i])) {
      return null
    }
    obj = obj[args[i]]
  }
  return obj
}